import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import {
  Home,
  Search,
  FileCheck,
  Euro,
  AlertTriangle,
  CheckCircle,
  ChefHat,
  Building,
  Building2,
  Users,
  Volume2 as VolumeIcon,
  Wind,
  ExternalLink,
  Briefcase,
  ArrowRight,
  Sparkles
} from 'lucide-react';

const FindingHousingPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white" data-testid="finding-housing-page">
      <Navbar />

      {/* Hero */}
      <section className="py-16 md:py-20 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-babyblue-200/40 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="container mx-auto max-w-4xl relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-babyblue-100 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Home className="w-4 h-4" />
            Integration Module
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-babyblue-800 mb-4">
            Finding Housing in Germany
          </h1>
          <p className="text-xl text-babyblue-600 leading-relaxed max-w-3xl mx-auto">
            The cultural shocks, the paperwork, and the rules that will save your deposit.
          </p>
        </div>
      </section>

      {/* Online portals */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Search className="w-7 h-7 text-blue-700" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-babyblue-800">Where to Search Online</h2>
            </div>
            <p className="text-babyblue-700 leading-relaxed mb-4">
              You can find apartments and houses online via the major real-estate search engines —{' '}
              <strong>Suchmaschinen für Wohnungen und Häuser</strong>. They list both rentals and properties for sale,
              with filters for city, price, size and special features (EBK, balcony, pets allowed, etc.).
            </p>
            <div className="bg-babyblue-50 rounded-xl p-5 border border-babyblue-200">
              <p className="text-babyblue-700 text-sm">
                💡 Tip: When searching, always cross-check listings on multiple platforms — and act fast. Good apartments in big cities go in hours, not days.
              </p>
            </div>
            <p className="text-sm text-babyblue-600 italic mt-4">
              💡 Most Germans <strong>rent</strong> their apartments — homeownership rates are far lower than in the UK or the US.
            </p>
          </div>
        </div>
      </section>

      {/* Bewerbungsmappe */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <FileCheck className="w-7 h-7 text-blue-700" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold text-babyblue-800">Die Bewerbungsmappe</h2>
                <p className="text-sm text-babyblue-600 italic">Your rental application folder</p>
              </div>
            </div>
            <p className="text-babyblue-700 leading-relaxed mb-6">
              Germany's rental market is <strong>highly competitive</strong>. Landlords expect a complete application folder upfront. Have these ready as PDFs <strong>before</strong> you visit a viewing — you'll be one of 20+ applicants.
            </p>

            <div className="space-y-3">
              {[
                {
                  title: 'Cover letter (Anschreiben)',
                  body: 'A short letter introducing yourself: your name, age, job, family situation, why you want this apartment. One page max.'
                },
                {
                  title: 'Schufa',
                  body: 'Your German credit record / financial-reliability proof. Must be no older than 2–3 months. You can order it at meineschufa.de.'
                },
                {
                  title: 'Einkommensnachweis',
                  body: 'Proof of income — usually your last 3 payslips, or an employment contract if you just started. Landlords look for monthly net income ≥ 3× the cold rent.'
                },
                {
                  title: 'Mietschuldenfreiheitsbescheinigung',
                  body: "Certificate from your previous landlord confirming you paid your rent on time and have no outstanding rent debt (no Mietrückstand)."
                },
                {
                  title: 'Copy of your ID',
                  body: 'Front and back side of your Personalausweis or passport.'
                },
              ].map((doc, i) => (
                <div key={i} className="bg-babyblue-50 border border-babyblue-200 rounded-xl p-4 flex gap-3" data-testid={`doc-${i}`}>
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-babyblue-800">{doc.title}</p>
                    <p className="text-sm text-babyblue-700 mt-0.5">{doc.body}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg mt-6">
              <p className="text-green-900">
                <strong>💸 Money-saving tip:</strong> A <strong>WG (Wohngemeinschaft)</strong> — shared flat — is dramatically cheaper than renting your own apartment. Everyone gets their own room and shares kitchen + bathroom. Super popular with students and young professionals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Einbauküche - cultural shock */}
      <section className="py-12 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-2xl p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-200 rounded-xl flex-shrink-0">
                <ChefHat className="w-8 h-8 text-amber-800" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-amber-700" />
                  <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">Cultural Shock</span>
                </div>
                <h2 className="font-heading text-2xl font-bold text-amber-900 mb-3">Die Einbauküche</h2>
                <p className="text-amber-900 leading-relaxed mb-4">
                  German apartments are rented <strong>empty</strong> — and that means <em>literally empty</em>. Often there's <strong>no kitchen installed</strong>: the previous tenant takes the kitchen, lamps, sometimes even sinks and curtain rails with them when they leave.
                </p>
                <p className="text-amber-900 leading-relaxed mb-4">
                  Your options: <strong>buy and install your own kitchen</strong>, or <strong>negotiate</strong> with the previous tenant to take theirs over (this is called <em>Ablöse</em> — you pay a one-time buy-out).
                </p>
                <div className="bg-white rounded-xl p-4 border-2 border-amber-300">
                  <p className="text-amber-900 font-semibold flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Look for <strong>"EBK"</strong> in listings
                  </p>
                  <p className="text-amber-900 text-sm mt-1">
                    EBK = Einbauküche. The kitchen is already installed and stays with the apartment.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cold vs Warm rent */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-babyblue-800 mb-2 text-center">
            Kaltmiete vs. Warmmiete
          </h2>
          <p className="text-babyblue-600 text-center mb-8">The single most confusing number on every German listing.</p>

          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6" data-testid="rent-kalt">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Euro className="w-6 h-6 text-blue-700" />
                </div>
                <h3 className="font-heading text-xl font-bold text-babyblue-800">Die Kaltmiete</h3>
              </div>
              <p className="text-sm text-babyblue-600 italic mb-3">"Cold rent" — the base rent</p>
              <p className="text-babyblue-700 leading-relaxed">
                The price for the <strong>physical apartment only</strong>. No utilities, no heating, no maintenance.
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl shadow-md border-2 border-orange-300 p-6" data-testid="rent-warm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-orange-200 rounded-lg">
                  <Euro className="w-6 h-6 text-orange-700" />
                </div>
                <h3 className="font-heading text-xl font-bold text-orange-900">Die Warmmiete</h3>
              </div>
              <p className="text-sm text-orange-700 italic mb-3">"Warm rent" — the actual amount you pay</p>
              <p className="text-orange-900 leading-relaxed">
                Warmmiete = Kaltmiete + <strong>Nebenkosten</strong> (additional charges): warm water, heating, trash collection, and building maintenance.
              </p>
            </div>
          </div>

          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg mt-6">
            <p className="text-red-900">
              <strong>⚠️ Pro tip:</strong> <strong>Electricity</strong> (Strom) and <strong>internet</strong> are usually <em>NOT</em> included in the Warmmiete. You'll have to set up both separately with providers like Vattenfall, E.ON, Telekom or 1&amp;1.
            </p>
          </div>
        </div>
      </section>

      {/* Types of housing */}
      <section className="py-12 px-6 bg-gradient-to-b from-babyblue-50/40 to-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-babyblue-800 mb-8 text-center">
            Types of Housing
          </h2>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-5" data-testid="house-altbau">
              <div className="flex items-center gap-3 mb-2">
                <Building className="w-6 h-6 text-amber-700" />
                <h3 className="font-bold text-babyblue-800 text-lg">Altbau</h3>
              </div>
              <p className="text-babyblue-700 text-sm">
                Built <strong>before WWII</strong>. Recognisable by <strong>high ceilings</strong> (often 3m+), large windows, thick walls, sometimes stucco. Charming but pricey to heat.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-5" data-testid="house-neubau">
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="w-6 h-6 text-blue-700" />
                <h3 className="font-bold text-babyblue-800 text-lg">Neubau</h3>
              </div>
              <p className="text-babyblue-700 text-sm">
                Built <strong>after WWII or recently</strong>. Modern facilities, better insulation, often with elevator and balcony.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-5" data-testid="house-wg">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-6 h-6 text-emerald-700" />
                <h3 className="font-bold text-babyblue-800 text-lg">Die WG (Wohngemeinschaft)</h3>
              </div>
              <p className="text-babyblue-700 text-sm">
                Hugely popular shared flat for young people. You get your own room but share the bathroom, kitchen and living room. Best place to make friends and practise German fast.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-5" data-testid="house-einfamilien">
              <div className="flex items-center gap-3 mb-2">
                <Home className="w-6 h-6 text-rose-700" />
                <h3 className="font-bold text-babyblue-800 text-lg">Einfamilienhaus</h3>
              </div>
              <p className="text-babyblue-700 text-sm">
                A standalone single-family house, usually in suburbs or smaller towns. Often comes with a small garden.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-5 md:col-span-2" data-testid="house-reihen">
              <div className="flex items-center gap-3 mb-2">
                <Building className="w-6 h-6 text-blue-700" />
                <h3 className="font-bold text-babyblue-800 text-lg">Reihenhaus</h3>
              </div>
              <p className="text-babyblue-700 text-sm">
                A townhouse — terraced row houses sharing side walls with neighbours. Common in cities; gives you a house feel at a lower price than an Einfamilienhaus.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Important rules */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-babyblue-800 mb-2 text-center">
            Important House Rules
          </h2>
          <p className="text-babyblue-600 text-center mb-8">Break these and you'll meet an angry neighbour — fast.</p>

          <div className="space-y-5">
            {/* Ruhezeit */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-50 border-2 border-blue-300 rounded-2xl p-6" data-testid="rule-ruhezeit">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-200 rounded-xl flex-shrink-0">
                  <VolumeIcon className="w-7 h-7 text-blue-800" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-blue-900 mb-2">Die Ruhezeit</h3>
                  <p className="text-blue-900 leading-relaxed mb-3">
                    <strong>10 PM – 6 AM, every day.</strong> Plus all day on <strong>Sunday</strong> in many buildings. A very strict rule: silence is expected.
                  </p>
                  <p className="text-blue-900 leading-relaxed">
                    That means <strong>no loud music, no washing machines, no vacuuming, no drilling.</strong> Neighbours can — and will — file complaints with the landlord or even the police if you ignore it.
                  </p>
                </div>
              </div>
            </div>

            {/* Stoßlüften */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-2xl p-6" data-testid="rule-stosslueften">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-emerald-200 rounded-xl flex-shrink-0">
                  <Wind className="w-7 h-7 text-emerald-800" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-emerald-900 mb-2">Stoßlüften</h3>
                  <p className="text-emerald-900 leading-relaxed mb-3">
                    You are <strong>expected</strong> to open your windows <strong>fully</strong> (not tilted!) for <strong>5–10 minutes, 2–3 times a day</strong> to ventilate the apartment and prevent mould (<em>Schimmel</em>).
                  </p>
                  <div className="bg-white rounded-xl p-4 border border-emerald-200">
                    <p className="text-emerald-900 font-semibold flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <span>Do not ignore this. If mould grows, your landlord can hold you responsible and deduct the repair costs from your deposit (<em>Kaution</em>).</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6 bg-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-xl font-bold text-babyblue-800 mb-6">Continue Your Integration</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { icon: '📄', title: 'Visa & Immigration', path: '/integration/visa' },
              { icon: '🏥', title: 'Healthcare System', path: '/integration/healthcare-system' },
              { icon: '💼', title: 'Career Paths', path: '/careers/business' },
            ].map((item, i) => (
              <Link
                key={i}
                to={item.path}
                className="bg-babyblue-50 rounded-xl border border-babyblue-200 p-4 flex items-center gap-3 hover:border-primary hover:shadow-md transition-all"
              >
                <span className="text-2xl">{item.icon}</span>
                <span className="text-babyblue-700 font-medium">{item.title}</span>
                <ArrowRight className="w-4 h-4 text-babyblue-400 ml-auto" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FindingHousingPage;
